import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import kayakFjordImage from '@/assets/kayak-fjord-macfarlane.jpg';

const GeographyOfWonder = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Nature Philosophy</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              The Geography of Wonder: Lessons from Robert Macfarlane's The Wild Places
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>October 30, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>12 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src={kayakFjordImage} 
              alt="Kayaker on Norwegian fjord at twilight - representing the wild places where wonder and wildness still exist"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
            <p className="absolute bottom-4 right-4 text-xs text-white/90 bg-black/40 px-3 py-1.5 rounded backdrop-blur-sm">
              Photo: Tomasz Furmanek
            </p>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic border-l-2 border-primary/30 pl-6">
              "The wild places are there, if we know how to look."<br />
              <span className="text-sm">— Robert Macfarlane, The Wild Places (Penguin Books, 2007, p. 119)</span>
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Where Has Wildness Gone?</h2>

              <p>
                At first glance, Robert Macfarlane's <em>The Wild Places</em> might seem like another travel book — a series of hikes through remote landscapes of Britain and Ireland. But as the journey unfolds, it becomes something far deeper: an exploration of perception, and a quiet protest against the idea that wildness is disappearing.
              </p>

              <p>
                Macfarlane begins his travels with a sense of loss. He worries that every corner of nature has been mapped, photographed, and domesticated — that there is no longer such a thing as the truly wild. But step by step, across coasts, moors, mountains, and forests, that belief begins to unravel.
              </p>

              <p className="border-l-2 border-primary/30 pl-6 italic">
                "I had thought the wild places of the world were gone," he writes,<br />
                "but the wildness had not gone. It was everywhere." (The Wild Places, 2007, p. 243)
              </p>

              <p>
                For Macfarlane, wild is not the opposite of civilized. It's not a distant mountain range or a blank spot on the map.<br />
                <strong className="text-accent">It's a way of paying attention.</strong>
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Inner Landscape of Attention</h2>

              <p>
                In an age where our senses are numbed by constant input, Macfarlane's writing feels like an act of resistance.<br />
                He doesn't chase danger or spectacle; instead, he practices the slow discipline of observation.
              </p>

              <p>
                He kneels to examine frost on a fence post, listens to a storm roll across a mountain, and sleeps under trees whose roots are centuries deep. The book's real journey happens not across miles, but in the sharpening of awareness.
              </p>

              <p>
                Psychologist Rachel Kaplan describes this as <strong className="text-accent">"soft fascination"</strong> — the mental state where attention is gently engaged, allowing the mind to rest and restore focus (Kaplan & Kaplan, <em>The Experience of Nature</em>, Cambridge University Press, 1989, p. 192).<br />
                It's the same phenomenon that modern neuroscience now associates with reduced stress, improved memory, and emotional balance.
              </p>

              <p>
                In other words, wildness — as Macfarlane describes it — is not something we visit.<br />
                <strong className="text-accent">It's something we cultivate within ourselves.</strong>
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Rewriting the Map of the Familiar</h2>

              <p>
                One of the most radical aspects of <em>The Wild Places</em> is its rejection of the myth that we must travel far to find meaning.<br />
                Macfarlane's discoveries happen not in jungles or deserts, but in hedgerows, under city trees, along forgotten rivers.
              </p>

              <p className="border-l-2 border-primary/30 pl-6 italic">
                He writes,<br /><br />
                "The wild places were not remote from civilization. They were folded into it."<br />
                (The Wild Places, Penguin Books, 2007, p. 271)
              </p>

              <p>
                This shift is both ecological and existential.<br />
                It asks us to look again at what we already inhabit — to find depth in the everyday.
              </p>

              <p>
                In a world obsessed with destinations, Macfarlane teaches dwelling.<br />
                He shows that the true wilderness is often invisible until we slow down enough to notice it — the curve of a path, the pattern of moss, the sound of a bird that doesn't care whether you've arrived.
              </p>

              <p>
                It's a perspective that echoes the principles of attention restoration theory (ART) and mindful ecology, fields that now confirm what Macfarlane felt intuitively:<br />
                <strong className="text-accent">that sustained attention to natural environments doesn't just relax us — it reorders the mind.</strong>
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Wildness as Relationship</h2>

              <p>
                There's a quiet humility that runs through every chapter of Macfarlane's work.<br />
                He doesn't frame nature as a backdrop for human self-discovery but as a living presence with agency and mystery.
              </p>

              <p>
                He learns that to truly experience the wild, one must abandon mastery — to listen rather than name, to sense rather than control.<br />
                This is what philosopher David Abram calls "the more-than-human world" — the recognition that we are not the center of the story but a small, sensing part of it (<em>The Spell of the Sensuous</em>, Vintage, 1996, p. 65).
              </p>

              <p className="border-l-2 border-primary/30 pl-6 italic">
                Macfarlane writes with reverence for this entanglement:<br /><br />
                "The wind made me its instrument; the hill spoke through me."<br />
                (The Wild Places, 2007, p. 207)
              </p>

              <p>
                For Mystic Forest, this perspective feels like a shared heartbeat.<br />
                The retreats and workshops we create are not escapes from reality, but spaces of reconnection — invitations to experience the same kind of awareness Macfarlane describes: grounded, sensory, and alive.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">What We Can Learn from the Wild</h2>

              <p>
                Macfarlane's work reminds us that "rewilding" begins not in politics or policy, but in perception.<br />
                It's about protecting not only ecosystems but also the psychological landscapes that allow us to feel wonder, stillness, and awe.
              </p>

              <p>
                It invites us to reclaim something our culture has almost forgotten —<br />
                that silence is not emptiness, and that wildness, however small, is essential to human wholeness.
              </p>

              <p className="border-l-2 border-primary/30 pl-6 italic text-xl">
                "We need wild places because we are wild places."<br />
                — The Wild Places (Penguin Books, 2007, p. 280)
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">In the End</h2>

              <p>
                Macfarlane doesn't ask us to go off the grid or vanish into the wilderness.<br />
                He asks us to look — really look — at what is already around us.
              </p>

              <p>
                To listen to the ground.<br />
                To remember that wonder is not a luxury, but a practice.<br />
                And that the wild is still here —<br />
                waiting, patient, everywhere.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• Macfarlane, Robert. <em>The Wild Places</em>. London: Penguin Books, 2007.</p>
                  <p>• Kaplan, Rachel, and Stephen Kaplan. <em>The Experience of Nature: A Psychological Perspective</em>. Cambridge University Press, 1989, p. 192.</p>
                  <p>• Abram, David. <em>The Spell of the Sensuous: Perception and Language in a More-Than-Human World</em>. Vintage, 1996, p. 65.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default GeographyOfWonder;
